import { useEffect, useRef, useState } from "react";
import { addDays, differenceInDays } from "date-fns";
import { debounce } from "lodash";
import Calendar, { DateInputDDMMYY } from "./Calendar";
import { PiArrowArcLeft } from "react-icons/pi";
import { IconClockHour5 } from "@tabler/icons-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { SqButton } from "@/components/squircle-ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { TimeInterval } from "@/types";
import type { DateRange, DateRangeWithLabel } from "@/utils/dateUtils";
import {
  getTimezoneDisplay,
  getPredefinedRanges,
  computeAutoCompareRange,
} from "@/utils/dateUtils";
import { RiCalendarLine, RiCloseLargeLine } from "@remixicon/react";

interface DateRangePickerProps {
  selected: DateRangeWithLabel | null;
  onSelect: (range: DateRangeWithLabel | null) => void;
  dateAggregate: number | "";
  defaultMonth?: Date;
  // Show/hide the sidebar with predefined ranges
  showSidebar?: boolean;
  onApply?: (
    range: DateRangeWithLabel | null,
    aggregate: number | "",
    aggregateDateChunks?: DateRange[] | null,
    compareRange?: DateRangeWithLabel | null,
  ) => void;
  onApplyToAllSections?: (
    range: DateRangeWithLabel | null,
    compareRange?: DateRangeWithLabel | null,
  ) => void;
  onCancel?: () => void;
  autoAllowed?: boolean;
  showAggregate?: boolean;
  first_allowed_date?: Date;
  last_allowed_date?: Date;
  hideCancelButton?: boolean;
  timezone: string;
  enableCompare?: boolean;
  onCompareDateChange?: (compareDate: DateRangeWithLabel | null) => void;
  compareDate?: DateRangeWithLabel;
  disableApply?: boolean;
  singleSelect?: boolean;
}

const DateRangePicker = ({
  defaultMonth,
  selected,
  dateAggregate,
  onSelect,
  showSidebar = true,
  onApply,
  onApplyToAllSections,
  onCancel,
  autoAllowed,
  showAggregate,
  first_allowed_date,
  last_allowed_date,
  hideCancelButton,
  timezone,
  enableCompare,
  onCompareDateChange,
  compareDate,
  disableApply,
  singleSelect = false,
}: DateRangePickerProps) => {
  const [dateRange, setDateRange] = useState<DateRangeWithLabel | null>(selected);
  const [aggregate, setAggregate] = useState<number | "">(dateAggregate ?? "");
  const [aggregateDateChunks, setAggregateDateChunks] = useState<DateRange[] | null>(
    null,
  );
  const [compare, setCompare] = useState<boolean>(Boolean(enableCompare && compareDate));
  const sidebarRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [calendarHeight, setCalendarHeight] = useState<number>(453);
  const [maxHeight, setMaxHeight] = useState<number>(800);

  // Get adjusted predefined ranges
  const adjustedPredefinedRanges = getPredefinedRanges(timezone);

  // Compute auto compare range mirroring Calendar's logic
  const computeAutoCompare = (
    currentRange: DateRangeWithLabel,
  ): DateRangeWithLabel | null => {
    return computeAutoCompareRange(currentRange, timezone, first_allowed_date);
  };

  const handlePredefinedRangeClick = (range: DateRangeWithLabel) => {
    // In single select mode, use only the start date
    const finalRange =
      singleSelect && range.from
        ? { from: range.from, to: range.from, label: range.label }
        : range;
    setDateRange(finalRange);
    if (compare && onCompareDateChange) {
      const auto = computeAutoCompare(finalRange);
      onCompareDateChange(auto);
    }
  };

  useEffect(() => {
    if (onSelect) {
      onSelect(dateRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  useEffect(() => {
    if (showSidebar && sidebarRef.current) {
      setCalendarHeight(sidebarRef.current.clientHeight);
    }
  }, [sidebarRef, showSidebar]);

  // Calculate max-height based on distance from top of div to bottom of viewport
  useEffect(() => {
    const calculateMaxHeight = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const distanceToBottom = window.innerHeight - rect.top;
        setMaxHeight(distanceToBottom);
      }
    };

    // Debounce the calculation to limit execution frequency during rapid events
    const debouncedCalculateMaxHeight = debounce(() => {
      requestAnimationFrame(calculateMaxHeight);
    }, 150);

    // Calculate on mount after layout (ensures Radix dropdown positioning is complete)
    requestAnimationFrame(() => {
      requestAnimationFrame(calculateMaxHeight);
    });
    window.addEventListener("resize", debouncedCalculateMaxHeight);
    window.addEventListener("scroll", debouncedCalculateMaxHeight);

    return () => {
      window.removeEventListener("resize", debouncedCalculateMaxHeight);
      window.removeEventListener("scroll", debouncedCalculateMaxHeight);
      debouncedCalculateMaxHeight.cancel();
    };
  }, []);

  useEffect(() => {
    setCompare(Boolean(enableCompare && compareDate));
  }, [compareDate, enableCompare]);

  // Enforce compare-off when current range starts at first_allowed_date
  useEffect(() => {
    if (!first_allowed_date) return;
    const start = dateRange?.from;
    if (start && start.getTime() === first_allowed_date.getTime()) {
      if (compare) setCompare(false);
      onCompareDateChange?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange?.from, first_allowed_date]);

  useEffect(() => {
    if (dateRange && aggregate && typeof aggregate === "number") {
      const chunks: DateRange[] = [];
      const totalDays = differenceInDays(dateRange.to, dateRange.from) + 1;
      const numberOfChunks = Math.ceil(totalDays / aggregate);

      // Calculate how many extra days we need to add to the start
      const remainder = totalDays % aggregate;
      const daysToAdjust = remainder === 0 ? 0 : aggregate - remainder;

      // Adjust start date backwards
      const adjustedStartDate = addDays(dateRange.from, -daysToAdjust);

      let chunkPointer = adjustedStartDate;

      for (let i = 0; i < numberOfChunks; i++) {
        const chunkStart = chunkPointer;
        const chunkEnd = addDays(chunkPointer, aggregate - 1);

        chunks.push({
          from: chunkStart,
          to: chunkEnd,
        });

        // Move to next chunk
        chunkPointer = addDays(chunkPointer, aggregate);
      }

      setAggregateDateChunks(chunks);
    } else {
      setAggregateDateChunks(null);
    }
  }, [aggregate, dateRange]);

  // Add helper function to check if range is valid
  const isRangeWithinAllowedDates = (range: DateRange) => {
    if (first_allowed_date && range.from < first_allowed_date) return false;
    if (last_allowed_date && range.to > last_allowed_date) return false;
    return true;
  };

  return (
    <div
      ref={containerRef}
      className="h-full overflow-y-auto flex flex-col rounded-[12px] p-[1px]  border-[1px] border-border-gray bg-white"
      style={{ maxHeight: `${maxHeight}px` }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-[6px] py-[4px]">
        <div className="flex items-center gap-[12px]">
          <div className="rounded-[6px] bg-[#FFF0EB] w-[28px] h-[28px] aspect-square flex items-center justify-center">
            <RiCalendarLine className="text-brand-orange shrink-0" size={14} />
          </div>
          <span className="text-brand-primary-800 text-[13px] font-medium leading-[19px] tracking-[0.28px]">
            {singleSelect ? "Select a date" : "Select a date range"}
          </span>
        </div>
        <div className="flex items-center gap-[24px] pr-4">
          {timezone && (
            <div className="flex items-center gap-[6px]">
              <IconClockHour5 className="text-[#9AA3BD]" size={16} />
              <p className="mt-[1px] line-clamp-1 text-[rgba(2,24,89,0.40)] text-[12px] font-normal leading-[19.157px] tracking-[0.28px]">
                <span className="text-brand-primary-new text-[12px] font-normal">
                  {getTimezoneDisplay(timezone)}
                </span>{" "}
                Time
              </p>
            </div>
          )}
          {onCancel && !hideCancelButton && (
            <SqButton
              variant={"outline"}
              size={"sm"}
              onClick={onCancel}
              className="h-[28px] w-[28px]"
            >
              <RiCloseLargeLine size={14} className="text-brand-primary-400 shrink-0" />
            </SqButton>
          )}
        </div>
      </div>
      <div className="flex-1 flex w-full">
        {/* Sidebar */}
        {showSidebar && (
          <div
            ref={sidebarRef}
            className="h-full w-[195px] py-[10px] flex flex-col gap-[4px] border-t-[0.5px] border-r-[0.5px] border-border-gray-dark"
          >
            {Object.entries(adjustedPredefinedRanges).map(([label, range]) => (
              <button
                key={label}
                disabled={
                  (label as TimeInterval) === TimeInterval.CUSTOM ||
                  (range ? !isRangeWithinAllowedDates(range) : false)
                }
                onClick={() =>
                  handlePredefinedRangeClick({ label: label as TimeInterval, ...range! })
                }
                className={cn(
                  "px-[9px] py-[5px] block text-left overflow-hidden text-brand-primary-new text-ellipsis text-[12px] font-normal leading-[17.241px] tracking-[0.252px]",
                  dateRange?.label === (label as TimeInterval)
                    ? "!text-brand-orange bg-[#FFF4F0] font-medium"
                    : "",
                  (label as TimeInterval) === TimeInterval.AUTO ? "hidden" : "",
                  range && !isRangeWithinAllowedDates(range)
                    ? "opacity-50 cursor-not-allowed"
                    : "",
                )}
              >
                <div className="flex justify-between items-center">
                  <span>{label}</span>
                </div>
              </button>
            ))}
            {/* Aggregate Input */}
            {showAggregate && (
              <div className="max-w-full flex px-[9px] py-[5.4px] items-center justify-between border-t-[0.9px] border-t-[#E1E6ED]">
                <label className="overflow-hidden text-brand-primary-new text-ellipsis text-[12.6px] font-normal leading-[17.241px] tracking-[0.252px]">
                  Agg. period <span className="text-[#617390]">(days)</span>
                </label>
                <input
                  type="number"
                  min={1}
                  value={aggregate}
                  placeholder="Auto"
                  onChange={(e) => setAggregate(Number(e.target.value) || "")}
                  className="w-[2.4rem] text-center placeholder:text-[12.6px] rounded-[1.8px] border-[0.9px] border-border-gray"
                />
              </div>
            )}
          </div>
        )}
        <div
          style={showSidebar ? { height: calendarHeight } : undefined}
          className="flex-1 max-h-[455px] min-w-[306px] overflow-hidden"
        >
          <Calendar
            dateRange={dateRange}
            setDateRange={setDateRange}
            aggregate={aggregate}
            defaultMonth={defaultMonth}
            aggregateDateChunks={aggregateDateChunks}
            first_allowed_date={first_allowed_date}
            last_allowed_date={last_allowed_date}
            timezone={timezone}
            compare={compare}
            compareDate={compareDate}
            onChangeCompareDate={(date) => {
              onCompareDateChange?.(date);
            }}
            singleSelect={singleSelect}
            hideInlineDateInputs
          />
        </div>
      </div>
      {/* Footer */}
      <div className="flex flex-col gap-2 px-[6px] py-[4px] border-t-[0.9px] border-t-border-gray-dark">
        {/* Compare Date Range Input */}
        {enableCompare && compare && (
          <div
            className={cn(
              "h-[42px] flex items-center justify-between gap-3 rounded-[8px] border border-[#CCD1DE] bg-[#F7F8FA] pl-[12px] pr-[6px] py-[6px] w-full",
              singleSelect && "justify-end",
            )}
          >
            <span className="text-[12px] text-brand-primary-800 font-medium whitespace-nowrap">
              Compare date range:
            </span>
            <div className="flex items-center gap-3">
              {!singleSelect && (
                <span className="text-[12px] text-brand-primary-800">From</span>
              )}
              <div
                className={cn(
                  "flex items-center justify-center gap-[10px] h-[30px] w-[120px] px-[20px] py-[6px] rounded-[6px] border border-[#CCD1DE] bg-white",
                )}
              >
                <DateInputDDMMYY
                  initialDate={compareDate?.from ?? null}
                  timezone={timezone}
                  first_allowed_date={first_allowed_date}
                  last_allowed_date={last_allowed_date}
                  onCommit={(d) => {
                    if (!onCompareDateChange || !compareDate) return;
                    const nextTo =
                      compareDate.to && compareDate.to < d ? d : compareDate.to || d;
                    onCompareDateChange({
                      from: d,
                      to: nextTo,
                      label: TimeInterval.CUSTOM,
                    });
                  }}
                  placeholder="Select date"
                  className="w-full bg-transparent text-[12px] text-brand-primary-800 font-medium placeholder-[#A0ABBC] outline-none"
                />
              </div>
              {!singleSelect && (
                <>
                  <span className="text-[12px] text-brand-primary-800">To</span>
                  <div
                    className={cn(
                      "flex items-center justify-center gap-[10px] h-[30px] w-[120px] px-[20px] py-[6px] rounded-[6px] border border-[#CCD1DE] bg-white",
                    )}
                  >
                    <DateInputDDMMYY
                      initialDate={compareDate?.to ?? null}
                      timezone={timezone}
                      first_allowed_date={first_allowed_date}
                      last_allowed_date={last_allowed_date}
                      onCommit={(d) => {
                        if (!onCompareDateChange || !compareDate) return;
                        const nextFrom =
                          compareDate.from && compareDate.from > d
                            ? d
                            : compareDate.from || d;
                        onCompareDateChange({
                          from: nextFrom,
                          to: d,
                          label: TimeInterval.CUSTOM,
                        });
                      }}
                      placeholder="Select date"
                      className="w-full bg-transparent text-[12px] text-brand-primary-800 font-medium placeholder-[#A0ABBC] outline-none"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Selected Date Range Input */}
        <div
          className={cn(
            "h-[42px] flex items-center justify-between gap-3 rounded-[8px] border border-[#FFDACC] bg-[#FFF9F7] pl-[12px] pr-[6px] py-[6px] w-full",
            singleSelect && "justify-end",
            enableCompare && compare && "",
          )}
        >
          <span className="text-[12px] text-brand-primary-800 font-medium whitespace-nowrap">
            {singleSelect ? "Selected date:" : "Selected date range:"}
          </span>
          <div className="flex items-center gap-3">
            {!singleSelect && (
              <span className="text-[12px] text-brand-primary-800">From</span>
            )}
            <div
              className={cn(
                "flex items-center justify-center gap-[10px] h-[30px] w-[120px] px-[20px] py-[6px] rounded-[6px] border border-[#FFDACC] bg-white",
              )}
            >
              <DateInputDDMMYY
                initialDate={dateRange?.from ?? null}
                timezone={timezone}
                first_allowed_date={first_allowed_date}
                last_allowed_date={last_allowed_date}
                onCommit={(d) =>
                  setDateRange((prev) => {
                    if (singleSelect) {
                      return {
                        from: d,
                        to: d,
                        label: TimeInterval.CUSTOM,
                      };
                    } else {
                      const previousTo = prev?.to || d;
                      const nextTo = previousTo < d ? d : previousTo;
                      return {
                        from: d,
                        to: nextTo,
                        label: TimeInterval.CUSTOM,
                      };
                    }
                  })
                }
                placeholder="Select date"
                className="w-full bg-transparent text-[12px] text-brand-primary-800 font-medium placeholder-[#A0ABBC] outline-none"
              />
            </div>
            {!singleSelect && (
              <>
                <span className="text-[12px] text-brand-primary-800">To</span>
                <div
                  className={cn(
                    "flex items-center justify-center gap-[10px] h-[30px] w-[120px] px-[20px] py-[6px] rounded-[6px] border border-[#FFDACC] bg-white",
                  )}
                >
                  <DateInputDDMMYY
                    initialDate={dateRange?.to ?? null}
                    timezone={timezone}
                    first_allowed_date={first_allowed_date}
                    last_allowed_date={last_allowed_date}
                    onCommit={(d) =>
                      setDateRange((prev) => {
                        const previousFrom = prev?.from || d;
                        const nextFrom = previousFrom > d ? d : previousFrom;
                        return {
                          from: nextFrom,
                          to: d,
                          label: TimeInterval.CUSTOM,
                        };
                      })
                    }
                    placeholder="Select date"
                    className="w-full bg-transparent text-[12px] text-brand-primary-800 font-medium placeholder-[#A0ABBC] outline-none"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Actions Row */}
        <div className="flex w-full justify-between items-center">
          {enableCompare && showSidebar && (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="compare-switch"
                      className="h-4 w-7 data-[state=unchecked]:bg-border-gray data-[state=checked]:bg-brand-orange"
                      thumbClassName="h-3 w-3 data-[state=checked]:translate-x-3 data-[state=unchecked]:translate-x-0.1"
                      onChange={(e) => console.log(e)}
                      checked={compare}
                      onCheckedChange={(checked) => {
                        setCompare(checked);
                        if (!checked) onCompareDateChange?.(null);
                      }}
                      disabled={
                        first_allowed_date &&
                        dateRange?.from?.getTime() === first_allowed_date.getTime()
                      }
                    />
                    <Label
                      htmlFor="compare-switch"
                      className={`text-[12px] leading-[19.157px] tracking-[0.28px] translate-y-[1px] font-medium ${
                        first_allowed_date &&
                        dateRange?.from?.getTime() === first_allowed_date.getTime()
                          ? "text-brand-primary-new/40"
                          : "text-brand-primary-new/80"
                      }`}
                    >
                      Compare
                    </Label>
                  </div>
                </TooltipTrigger>
                {first_allowed_date &&
                  dateRange?.from?.getTime() === first_allowed_date.getTime() && (
                    <TooltipContent
                      className="squircle squircle-mask squircle-fill-[black]"
                      side="bottom"
                      align="start"
                    >
                      Previous dates not available
                    </TooltipContent>
                  )}
              </Tooltip>
            </TooltipProvider>
          )}
          {autoAllowed && (
            <div className="flex px-[8px] py-[5px] justify-center items-center gap-[6px]">
              <button
                disabled={!dateRange}
                onClick={() => {
                  setDateRange(null);
                  setAggregate("");
                  setAggregateDateChunks(null);
                  if (onApply) onApply(null, "", null);
                }}
              >
                <div className="flex items-center gap-1 text-brand-primary-new text-[12px] font-normal leading-[19.157px] tracking-[0.28px]">
                  <span>
                    <PiArrowArcLeft />
                  </span>
                  <span>Reset to Auto</span>
                </div>
              </button>
            </div>
          )}
          <div
            className={cn(
              "flex justify-end items-center gap-2 flex-1",
              (!showSidebar || !enableCompare) && "w-full",
            )}
          >
            {onApplyToAllSections && (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SqButton
                      variant="outline"
                      size="md"
                      onClick={() =>
                        onApplyToAllSections(dateRange, compare ? compareDate : null)
                      }
                      className="cursor-pointer squircle-mask font-medium text-brand-primary-800"
                      disabled={disableApply}
                    >
                      Apply to All Sections
                    </SqButton>
                  </TooltipTrigger>
                  <TooltipContent
                    align="start"
                    side="bottom"
                    className="squircle squircle-fill-[black]"
                  >
                    Applies dates to all sections
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <SqButton
                    variant="fill"
                    size="md"
                    onClick={() =>
                      onApply &&
                      onApply(
                        dateRange,
                        aggregate,
                        aggregateDateChunks,
                        compare ? compareDate : null,
                      )
                    }
                    className="cursor-pointer squircle-mask font-medium"
                    disabled={disableApply}
                  >
                    Apply
                  </SqButton>
                </TooltipTrigger>
                <TooltipContent
                  align="start"
                  side="bottom"
                  className="squircle squircle-fill-[black]"
                >
                  Applies dates only to this section
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;
